import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./css/transformationGame.css";
import useGameState from "../hooks/useGameState";
import useTranslation from "../hooks/useTranslation";

// Recursos
import edificos from "../assets/recursosJuego/EDIFICIOS-01.png";
import gameLogo from "../assets/recursosJuego/greenescape-logo.png";

// Importar todas las imágenes en WebP (más ligeras)
import imagen1 from "../assets/recursosJuego/1.webp";
import imagen2 from "../assets/recursosJuego/2.webp";
import imagen3 from "../assets/recursosJuego/3.webp";
import imagen4 from "../assets/recursosJuego/4.webp";
import imagen5 from "../assets/recursosJuego/5.webp";
import imagen6 from "../assets/recursosJuego/6.webp";
import imagen7 from "../assets/recursosJuego/7.webp";
import imagen8 from "../assets/recursosJuego/8.webp";
import imagen9 from "../assets/recursosJuego/9.webp";
import imagen10 from "../assets/recursosJuego/10.webp";
import imagen11 from "../assets/recursosJuego/11.webp";
import imagen12 from "../assets/recursosJuego/12.webp";
import imagen13 from "../assets/recursosJuego/13.webp";
import imagen14 from "../assets/recursosJuego/14.webp";
import imagen15 from "../assets/recursosJuego/15.webp";
import imagen16 from "../assets/recursosJuego/16.webp";
import imagen17 from "../assets/recursosJuego/17.webp";
import imagen18 from "../assets/recursosJuego/18.webp";

// Array de todas las imágenes disponibles
const heroImages = [
  imagen1, imagen2, imagen3, imagen4, imagen5, imagen6, imagen7, imagen8, imagen9,
  imagen10, imagen11, imagen12, imagen13, imagen14, imagen15, imagen16, imagen17, imagen18
];

function TransformationGame() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { selectedCharacters } = useGameState();
  const players = selectedCharacters?.playersArray || [];
  
  // Función helper para obtener el nombre traducido del personaje
  const getCharacterName = (characterId) => {
    return t(`characterSelection.characters.${characterId}.name`);
  };
  
  // Estado para las iniciativas
  const [currentInitiativeIndex, setCurrentInitiativeIndex] = useState(0);
  const [initiative, setInitiative] = useState("");
  const [initiatives, setInitiatives] = useState([]); // Array para guardar todas las iniciativas
  const [usedImages, setUsedImages] = useState([]);
  const [currentHeroImage, setCurrentHeroImage] = useState(() => {
    // Seleccionar imagen aleatoria al inicio
    const randomIndex = Math.floor(Math.random() * heroImages.length);
    return heroImages[randomIndex];
  });

  const totalInitiatives = 5;

  // Array de temas para cada iniciativa
  const initiativeTopics = t('transformationGame.initiatives');

  // Función para obtener la siguiente imagen sin repetir recientes
  const getNextImage = () => {
    let availableImages = heroImages.filter(img => !usedImages.includes(img));
    
    // Si ya usamos todas las imágenes, reiniciar pero mantener la actual fuera
    if (availableImages.length === 0) {
      availableImages = heroImages.filter(img => img !== currentHeroImage);
      setUsedImages([]);
    }
    
    // Seleccionar aleatoriamente de las disponibles
    const randomIndex = Math.floor(Math.random() * availableImages.length);
    const newImage = availableImages[randomIndex];
    
    // Agregar a las usadas
    setUsedImages(prev => [...prev, newImage]);
    
    return newImage;
  };

  // Función para manejar el envío de la iniciativa
  const handleSubmitInitiative = () => {
    if (!initiative.trim()) {
      console.log("La iniciativa no puede estar vacía");
      return;
    }

    console.log(`Iniciativa ${currentInitiativeIndex + 1} enviada:`, initiative);
    
    // Guardar la iniciativa actual
    setInitiatives(prev => [...prev, initiative]);
    
    // Limpiar el textarea
    setInitiative("");

    // Verificar si hay más iniciativas
    if (currentInitiativeIndex < totalInitiatives - 1) {
      // Pasar a la siguiente iniciativa
      setCurrentInitiativeIndex(currentInitiativeIndex + 1);
      // Cambiar la imagen
      const newImage = getNextImage();
      setCurrentHeroImage(newImage);
    } else {
      // Se terminaron todas las iniciativas (5/5)
      console.log("¡Todas las iniciativas completadas!", initiatives);
      
      // Guardar todas las iniciativas en localStorage
      localStorage.setItem('greenscape_transformation_initiatives', JSON.stringify([...initiatives, initiative]));
      
      // Navegar a la pantalla de felicitaciones
      navigate('/congratulations');
    }
  };

  return (
    <div className="transformation-game-wrapper">
      <div className="transformation-game-layout">
        {/* Sidebar izquierda */}
        <aside className="game-sidebar">
          <div className="fraction">
            <div className="big">{currentInitiativeIndex + 1}</div>
            <div className="small">/ {totalInitiatives}</div>
          </div>
          <div className="mode">{t('transformationGame.mode')}</div>
          <ul className="turn-list">
            {players.map((p) => (
              <li key={p.characterId} className="turn-item all-active">
                <div className="turn-item-content">
                  <img className="turn-avatar" src={p.image} alt={getCharacterName(p.characterId)} />
                  <div className="turn-text-container">
                    <div className="turn-label">{t('transformationGame.yourTurnLabel')}</div>
                    <span className="turn-name">{getCharacterName(p.characterId)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </aside>

        {/* Zona principal */}
        <main className="game-main">
          <div className="hero">
            <img src={currentHeroImage} alt="Hero" className="hero-img" />
            <img src={edificos} alt="Skyline" className="hero-skyline" />
          </div>
        </main>
      </div>

      {/* Franja inferior de pregunta - ancho completo */}
      <section className="question-strip">
        <div className="qs-main-grid">
          {/* Columna izquierda: Logo + Contenido principal */}
          <div className="qs-content-left">
            <div className="qs-logo-area">
              <img src={gameLogo} alt="GreenScape" className="qs-logo" />
            </div>
            <div className="qs-main-content">
              <h2 className="topic-title">{initiativeTopics[currentInitiativeIndex].title}</h2>
              <p className="topic-text">{initiativeTopics[currentInitiativeIndex].text}</p>
              {initiativeTopics[currentInitiativeIndex].prompt && (
                <p className="topic-prompt">{initiativeTopics[currentInitiativeIndex].prompt}</p>
              )}
              
              {/* Consigna debajo del prompt */}
              <div className="consigna-section">
                <strong className="consigna-title">{t('transformationGame.consignaTitle')}</strong>
                <p className="consigna-text">{initiativeTopics[currentInitiativeIndex].consigna}</p>
              </div>
              
              {/* Área de respuesta con textarea y botón */}
              <div className="initiative-area">
                <textarea
                  className="initiative-textarea"
                  placeholder={t('transformationGame.textareaPlaceholder')}
                  value={initiative}
                  onChange={(e) => setInitiative(e.target.value)}
                  maxLength={350}
                  rows={5}
                />
                <button 
                  className="submit-initiative-btn"
                  onClick={handleSubmitInitiative}
                  disabled={!initiative.trim()}
                >
                  {t('transformationGame.submitButton')}
                </button>
              </div>
            </div>
          </div>
          
          {/* Columna derecha: Solo Ejemplos */}
          <div className="qs-content-right">
            <div className="examples-section">
              <strong className="examples-title">{t('transformationGame.examplesTitle')}</strong>
              <ul className="examples-list">
                {initiativeTopics[currentInitiativeIndex].examples?.map((example, index) => (
                  <li key={index}>{example}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TransformationGame;

